import { useDisclosure } from '@chakra-ui/hooks';
import { usePopper } from '@chakra-ui/popper';
import {
  Box,
  BoxProps,
  Flex,
  FormControlOptions,
  InputProps,
  Text,
  chakra,
  useMultiStyleConfig,
  useStyleConfig,
} from '@chakra-ui/react';
import { mergeWith } from '@chakra-ui/utils';
import { FieldError } from 'features';
import { ReactNode, useEffect, useMemo, useRef, useState } from 'react';

type omitInputProps = 'value' | 'onChange' | 'defaultValue' | 'name' | 'ref';

export interface AutoCompleteProps<TItem = any, TValue = any>
  extends Omit<BoxProps, 'onChange'>,
    FormControlOptions {
  name: string | undefined;
  valueKey: (item: TItem) => TValue;
  textKey: (item: TItem) => string;
  items: TItem[] | undefined;
  value: TValue | undefined;
  onChange: (item: TValue | undefined) => void;
  inputProps?: Omit<InputProps, omitInputProps>;
  render?: (item: TItem, meta: AutoCompleteItem<TItem, TValue>) => ReactNode;
  focusOnMount?: boolean;
  placeholder?: string;
  mountErrorField?: boolean;
}

export interface AutoCompleteItem<TItem = any, TValue = any> {
  item: TItem;
  value: TValue;
  index: number;
  text: string;
}

export const AutoComplete = <TItem extends any = any, TValue extends any = any>(
  props: AutoCompleteProps<TItem, TValue>
) => {
  const {
    valueKey,
    textKey,
    items = [] as TItem[],
    value,
    onChange,
    inputProps,
    render,
    focusOnMount,
    placeholder,
    isDisabled,
    mountErrorField = true,
    ...rest
  } = props;

  // useEffect(() => {
  //   // console.debug('AutoComplete props', props);
  //   // console.debug('AC Items', items);
  // }, [items]);

  const { isOpen, onClose, onOpen } = useDisclosure();
  const inputRef = useRef<HTMLInputElement>();
  const styles = useMultiStyleConfig('CustomSelect', props);
  const [activeItem, setActiveItem] = useState<AutoCompleteItem>();
  const [text, setText] = useState('');
  const { referenceRef: popperRef, getPopperProps } = usePopper({});

  const mappedItems = useMemo(
    () =>
      items?.map(
        (x, i) =>
          ({
            index: i,
            item: x,
            value: valueKey(x),
            text: textKey(x),
          } as AutoCompleteItem<TItem, TValue>)
      ),
    [items, textKey, valueKey]
  );

  const selectedItem = useMemo(() => {
    const result = mappedItems.find((x) => x.value === value);
    return result;
  }, [mappedItems, value]);

  const filteredItems = useMemo(
    () =>
      mappedItems.filter((mappedItem) => {
        const longText = mappedItem.text;
        return startsWith(text, longText);
      }),
    [mappedItems, text]
  );

  const handleBlur = (e: any) => {
    onClose();
    if (activeItem?.value !== selectedItem?.value) {
      onChange(activeItem?.value);
    } else {
      setText(activeItem?.text ?? '');
    }
  };

  const handleKeyDown = (e: any) => {
    if (!isOpen) return;
  };
  const handleFocus = (e: any) => {
    onOpen();
  };
  const handleInputFocus = (e: any) => {
    if (!inputRef) return;
    inputRef.current?.setSelectionRange(0, inputRef.current.value.length);
  };

  // Change text when selected item / value changes
  useEffect(() => setText(selectedItem?.text ?? ''), [selectedItem?.text]);

  // focus the input on mount
  useEffect(() => {
    if (focusOnMount) {
      inputRef.current?.focus();
    }
  }, [inputRef, focusOnMount]);

  // pre-select the selected item if in filter, or else first item in filter on filter change
  useEffect(() => {
    setTimeout(() => {
      if (selectedItem) {
        if (startsWith(text, selectedItem.text)) {
          setActiveItem(selectedItem);
          return;
        }
      }
      setActiveItem(filteredItems.at(0));
    }, 50);
  }, [filteredItems, selectedItem, text]);

  return (
    <>
      <chakra.div
        {...rest}
        position={'relative'}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
      >
        <chakra.input
          {...inputProps}
          disabled={isDisabled}
          __css={styles.field}
          value={text}
          data-focus-visible-added={isOpen}
          onChange={(e) => {
            setText(e.target.value);
          }}
          placeholder={placeholder}
          ref={(e) => {
            popperRef(e);
            (inputRef.current as any) = e;
          }}
          onFocus={handleInputFocus}
        />
        {isOpen && !isDisabled && (
          <chakra.div zIndex={1} width="100%" {...mergeWith(getPopperProps())}>
            <chakra.ul __css={styles.menu} data-focus-visible-added={isOpen}>
              {filteredItems.map((item, ind) => (
                <AutoCompleteBox
                  key={ind}
                  px={[2, 4]}
                  py={[1, 2]}
                  onMouseEnter={() => setActiveItem(item)}
                  onMouseLeave={() => setActiveItem(undefined)}
                  onMouseDown={() => onChange(item.value)}
                  cursor="pointer"
                  variant={
                    value === item.value
                      ? 'selected'
                      : activeItem?.value === item.value
                      ? 'outline'
                      : ''
                  }
                >
                  {render?.(item.item, item) ?? (
                    <AutoCompleteItemComponent {...item} match={text.length} />
                  )}
                </AutoCompleteBox>
              ))}
            </chakra.ul>
          </chakra.div>
        )}
      </chakra.div>
      {mountErrorField && <FieldError name={props.name} />}
    </>
  );
};

function startsWith(prefix: string, long: string) {
  return long.toLocaleLowerCase().startsWith(prefix.toLocaleLowerCase());
}

function AutoCompleteBox(props: BoxProps & { variant: string }) {
  const { variant, ...rest } = props;
  const style = useStyleConfig('AutoCompleteItemBox', { variant });
  return <Box __css={style} {...rest} />;
}

function AutoCompleteItemComponent<TValue = any, TId = any>(
  props: AutoCompleteItem<TValue, TId> & { match: number }
) {
  const { text, match } = props;
  const prefix = text.substring(0, match);
  const suffix = text.substring(match);
  return (
    <Flex gap={0}>
      <Text as="b">{prefix}</Text>
      <Text>{suffix}</Text>
    </Flex>
  );
}
